import { LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Spin, Tooltip } from "antd";
import {orderBy, sortBy, uniqBy} from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaInfo } from "react-icons/fa6";
import { IoChatbubbleEllipsesOutline, IoPerson } from "react-icons/io5";
import { MdMailOutline } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import apiFactory from "../../../api";
import { conversationTab, conversationType } from "../../../config/Constant";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { openNotificationError, showName } from "../../../utils/Utils";
import { UserDetailModal } from "../../avatar/UserDetailModal";
import { useDirectoryContext } from "./DirectoryContext";
import "./style.scss";
import { MessageAvatar } from "../../avatar/MessageAvatar";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import searchImg from "../../../assets/search.png";

const QuickOrganization = ({ onlyView }) => {
  const navigate = useNavigate();
  const { tree, searchResult, setSearchResult, isTreeLoading } =
    useDirectoryContext();
  const { user } = useInfoUser();
  const organizationRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const {
    handleUpdateConversationList,
    conversationList,
    activeKey,
    selectedConversation,
  } = useMenuContext();

  const createPersonalChat = async (person) => {
    setLoading(true);

    if (
      (person?.userCode !== user?.userId &&
        selectedConversation?.memberList?.includes(person?.userCode)) ||
      (selectedConversation?.type === conversationType.SELF &&
        person?.userCode === user?.userId)
    ) {
      setLoading(false);
      return;
    }

    const userIds =
      user?.userId === person?.userCode
        ? [user?.userId]
        : [user?.userId, person?.objectId];

    const type =
      user?.userId === person?.userCode
        ? conversationType.SELF
        : conversationType.PERSONAL;

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: userIds,
      conversationType: type,
    });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setLoading(false);
    } else {
      setLoading(false);

      if (result?.data === null) {
        navigate(`/conversation/virtual/${person?.objectId}`);
        return;
      }

      if (result?.data?.type === conversationType.PERSONAL) {
        const isExist = conversationList?.some(
          (item) => item?.conversationId === result?.data?.conversationId,
        );

        if (!isExist && activeKey === conversationTab.ALL) {
          handleUpdateConversationList([result?.data, ...conversationList]);
        } else {
          const updatedList = [
            result?.data,
            ...conversationList.filter(
              (item) => item?.conversationId !== result?.data?.conversationId,
            ),
          ];

          handleUpdateConversationList(updatedList);
        }
      }

      navigate(`/conversation/chat/${result?.data?.conversationId}`);
    }
  };

  useEffect(() => {
    if (searchResult?.length) {
      organizationRef?.current[searchResult?.[0]?.objectId]?.scrollIntoView();
      setSearchResult([]);
    }
  }, [tree]);

  return (
    <>
      <div ref={organizationRef} className="quick-org">
        {sortBy(tree, ["position"])?.map((item) => (
          <div
            ref={(el) => {
              if (organizationRef?.current)
                return (organizationRef.current[item?.objectId] = el);
            }}
            key={item?.id}
          >
            <Subtree
              onlyView={onlyView}
              item={item}
              key={item?.id}
              organizationRef={organizationRef}
              createPersonalChat={createPersonalChat}
              setUserInfo={setUserInfo}
            />
          </div>
        ))}
        {isTreeLoading && (
          <div className="absolute top-[50%] w-full text-center">
            <Spin indicator={<LoadingOutlined classID="text-[35px]" spin />} />
          </div>
        )}
      </div>
      {!!userInfo && (
        <UserDetailModal
          person={userInfo}
          isOpen={!!userInfo}
          handleCancel={() => setUserInfo(null)}
        />
      )}
    </>
  );
};

const Subtree = ({
  item,
  onlyView,
  organizationRef,
  createPersonalChat,
  setUserInfo,
}) => {
  const { openFolder, onClickCheckBox, onClickFolder } = useDirectoryContext();
  const navigate = useNavigate();
  const { selectedConversation } = useMenuContext();
  const { user, languageMap } = useInfoUser();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const recipient = queryParams.get("recipient");
  const isChat = location.pathname.includes("/chat");

  const isHighlighted =
    (selectedConversation?.memberList?.includes(item?.userCode) &&
      item?.userCode !== user?.userId) ||
    recipient === item?.objectId;

  const comeToMail = (e) => {
    e.stopPropagation();
    navigate(`/conversation/draft-mail?recipient=${item?.userCode}`);
  };

  return (
    <div
      className="subtree text-[14px]"
      ref={(el) => {
        if (organizationRef && organizationRef.current)
          return (organizationRef.current[item?.objectId] = el);
      }}
    >
      <div
        className={`parent !w-[98%] !items-start rounded-md hover-user-select
           ${isHighlighted ? "quick-parent-focus" : "quick-parent"}
            `}
        onClick={() => onClickFolder(item)}
      >
        {!onlyView && (
          <Checkbox
            onClick={(e) => onClickCheckBox(e, item)}
            checked={item?.checked}
            indeterminate={item?.indeterminate}
          />
        )}

        {item?.type === "INSTITUTION" && openFolder(item)}

        {item?.type !== "INSTITUTION" && (
          <div
            className={
              item?.highlight
                ? "text-[#eda30f] flex flex-col w-full"
                : "text-[black] flex flex-col w-full "
            }
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-1">
                {item?.type !== "INSTITUTION" && (
                  <IoPerson
                    size={20}
                    color={`${item?.online ? "#24832c" : "black"}`}
                  />
                )}
                {showName(
                  `${item?.name || ""}${item?.userPosition ? ` - ${item?.userPosition}` : ""}`,
                )}
              </div>

              <div className="flex gap-1 button-org-msg">
                <Tooltip
                  placement="bottom"
                  title={"사용자 정보"}
                  color={"#0091ff"}
                >
                  <Button
                    type="default"
                    shape="circle"
                    className={`border-none text-[#2a56b9] bg-transparent`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setUserInfo({ ...item, userId: item?.userCode });
                    }}
                    icon={<FaInfo className="text-[15px]" />}
                  />
                </Tooltip>
                <Tooltip
                  placement="bottom"
                  title={
                    languageMap?.["menu.conversation.createMail"] ??
                    "Create mail"
                  }
                  color={"#0091ff"}
                >
                  <Button
                    shape="circle"
                    className={`border-none text-[#2a56b9]
                         ${
                           isHighlighted && !isChat
                             ? "bg-white"
                             : "bg-transparent "
                         }`}
                    onClick={comeToMail}
                    icon={<MdMailOutline className="text-[18px]" />}
                  />
                </Tooltip>
                <Tooltip
                  placement="bottom"
                  title={
                    languageMap?.["modal.organization.createGroupName"] ??
                    "Create group"
                  }
                  color={"#0091ff"}
                >
                  <Button
                    type="default"
                    shape="circle"
                    className={`border-none text-[#2a56b9] ${
                      isHighlighted && isChat ? "bg-white" : "bg-transparent "
                    }`}
                    onClick={() => {
                      createPersonalChat(item);
                    }}
                    icon={
                      <IoChatbubbleEllipsesOutline className="text-[18px]" />
                    }
                  />
                </Tooltip>
              </div>
            </div>

            <div className="text-[#2a55b9] flex flex-col ml-[15px]">
              {item?.phone && (
                <span className="text-[#2a55b9]">• {showName(item.phone)}</span>
              )}
              {item?.mood && (
                <span className="text-[#2a55b9]">• {showName(item.mood)}</span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="children">
        {item?.isOpen &&
          sortBy(item?.children, ["position"])?.map((i) => (
            <Subtree
              item={i}
              openFolder={openFolder}
              key={i?.id}
              onClickCheckBox={onClickCheckBox}
              onlyView={onlyView}
              organizationRef={organizationRef}
              createPersonalChat={createPersonalChat}
              setUserInfo={setUserInfo}
            />
          ))}
      </div>
    </div>
  );
};

const QuickOnlineList = ({ activeTab, searchQuery }) => {
  const { orgList } = useAuthLayoutContext();

  const onlineList = useMemo(() => {
    return orderBy(
      uniqBy(
        orgList.filter(
          (org) =>
            org?.type === "PERSON" &&
            org?.online &&
            (org?.name?.includes(searchQuery) ||
              org?.userCode?.includes(searchQuery)),
        ),
        "userCode",
      ),
      ["loginTime"],
      ["desc"],
    );
  }, [orgList, searchQuery]);

  if (activeTab !== "online") return null;

  if (!onlineList?.length)
    return (
      <div className="message-list">
        <div className="flex flex-col items-center">
          <img src={searchImg} alt="Cover" className="w-[40%]" />
          <div className="px-[44px] text-center text-[#5a6981]">
            No results found
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <div className="flex flex-col gap-[10px] overflow-auto max-h-[89vh]">
        {onlineList?.map((onliner) => (
          <QuickOnline onliner={onliner} key={onliner?.userCode} />
        ))}
      </div>
    </div>
  );
};

const QuickOnline = ({ onliner }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { getMember } = useAuthLayoutContext();

  const { user, languageMap } = useInfoUser();
  const {
    handleUpdateConversationList,
    conversationList,
    activeKey,
    selectedConversation,
  } = useMenuContext();

  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const recipient = queryParams.get("recipient");
  const isChat = location.pathname.includes("/chat");

  const isHighlighted =
    (selectedConversation?.memberList?.includes(onliner?.userCode) &&
      onliner?.userCode !== user?.userId) ||
    recipient === onliner?.objectId;

  const comeToMail = (e) => {
    e.stopPropagation();
    navigate(`/conversation/draft-mail?recipient=${onliner?.userCode}`);
  };

  const createPersonalChat = async (person) => {
    setLoading(true);

    if (
      (person?.userCode !== user?.userId &&
        selectedConversation?.memberList?.includes(person?.userCode)) ||
      (selectedConversation?.type === conversationType.SELF &&
        person?.userCode === user?.userId)
    ) {
      setLoading(false);
      return;
    }

    const userIds =
      user?.userId === person?.userCode
        ? [user?.userId]
        : [user?.userId, person?.objectId];

    const type =
      user?.userId === person?.userCode
        ? conversationType.SELF
        : conversationType.PERSONAL;

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: userIds,
      conversationType: type,
    });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setLoading(false);
    } else {
      setLoading(false);

      if (result?.data === null) {
        navigate(`/conversation/virtual/${person?.userCode}`);
        return;
      }

      if (result?.data?.type === conversationType.PERSONAL) {
        const isExist = conversationList?.some(
          (item) => item?.conversationId === result?.data?.conversationId,
        );

        if (!isExist && activeKey === conversationTab.ALL) {
          handleUpdateConversationList([result?.data, ...conversationList]);
        } else {
          const updatedList = [
            result?.data,
            ...conversationList.filter(
              (item) => item?.conversationId !== result?.data?.conversationId,
            ),
          ];

          handleUpdateConversationList(updatedList);
        }
      }

      navigate(`/conversation/chat/${result?.data?.conversationId}`);
    }
  };

  return (
    <div
      key={selectedConversation?.conversationId}
      // className={`flex items-center gap-2 parent !w-[98%] rounded-md hover-user-select
      //      ${isHighlighted ? "quick-parent-focus" : "quick-parent"}`}

      className={`flex onliner items-center gap-2 px-[10px] py-[6px] hover-user-select
           ${isHighlighted ? "quick-parent-focus" : "quick-parent"}`}
    >
      <MessageAvatar
        person={getMember(onliner?.userCode)}
        size={50}
        styleCss={{}}
      />
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-[10px]">
        <div className="conversation-name flex justify-between items-center gap-2">
          <div className="name truncate flex-1 font-semibold">
            {showName(
              `${onliner?.name || ""}${onliner?.userPosition ? ` - ${onliner?.userPosition}` : ""}`,
            )}
          </div>

          <div className="time-and-bell text-[12px] conversation-item flex items-center shrink-0 gap-1">
            <div className="flex gap-1 button-org-msg">
              <Tooltip
                placement="bottom"
                title={"사용자 정보"}
                color={"#0091ff"}
              >
                <Button
                  type="default"
                  shape="circle"
                  className={`border-none text-[#2a56b9] bg-transparent`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserInfo({ ...onliner, userId: onliner?.userCode });
                  }}
                  icon={<FaInfo className="text-[15px]" />}
                />
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={
                  languageMap?.["menu.conversation.createMail"] ?? "Create mail"
                }
                color={"#0091ff"}
              >
                <Button
                  shape="circle"
                  className={`border-none text-[#2a56b9]
                                       ${
                                         isHighlighted && !isChat
                                           ? "bg-white"
                                           : "bg-transparent "
                                       }`}
                  onClick={comeToMail}
                  icon={<MdMailOutline className="text-[18px]" />}
                />
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={
                  languageMap?.["modal.organization.createGroupName"] ??
                  "Create group"
                }
                color={"#0091ff"}
              >
                <Button
                  type="default"
                  shape="circle"
                  className={`border-none text-[#2a56b9] ${
                    isHighlighted && isChat ? "bg-white" : "bg-transparent "
                  }`}
                  onClick={() => {
                    createPersonalChat(onliner);
                  }}
                  icon={<IoChatbubbleEllipsesOutline className="text-[18px]" />}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="text-[#2a55b9] flex flex-col gap-[5px]">
          {onliner?.paths?.length && (
            <div className="text-[#2a56b9]">
              {onliner?.paths?.map((path) => path?.name)?.join(" > ")}
            </div>
          )}
        </div>
      </div>
      {!!userInfo && (
        <UserDetailModal
          person={userInfo}
          isOpen={!!userInfo}
          handleCancel={() => setUserInfo(null)}
        />
      )}
    </div>
  );
};

export { QuickOrganization, QuickOnlineList };
